import React from 'react';
import { CardInfo } from '../../components/CardInfo';
import ChartProgressBar from '../../components/ChartProgressBar';
import CompositionWalletCard from '../../components/CompositionWalletCard';
import { useNumberFormatter } from '../../../../../hooks/useNumberFormatter';
import { useTranslation } from 'react-i18next';
import HeaderReport from '../../components/HeaderReport';
import ReusableIcon from '../../../../Reports/ExtemdedReport/components/shared/ReusableIcon';
import PendingCard from '../../../../Reports/ExtemdedReport/components/GeneralInformation/shared/PendingCard';
import { isMinibizableGrid } from '../../utils/compositionChart';

export const gradientColors = [
  '#0b396e',
  '#3975c8',
  '#4fa0a5',
  '#f39f2e',
  '#ffd700',
  '#27BAC7',
  '#BE86D8',
  '#FA3938',
  '#91E2A1',
  '#F06D6D',
  '#538CFF',
  '#E58EB8',
  '#B1CBFF',
  '#D0DEF9',
];

interface OnePagerReportProps {
  data: {
    loan?: any;
    igr?: any;
    tcp?: any;
  };
  generalData: any;
  title: string;
  filtersApplied: any;
  portfolioName: any;
  isPdfExport?: boolean;
}

const OnePagerReportTcp = ({ data, title, generalData, filtersApplied, portfolioName, isPdfExport = false }: OnePagerReportProps) => {
  const { t } = useTranslation();
  const { formatMillions } = useNumberFormatter();

  const renderSection = (sectionData: any, title: string) => {
    if (!sectionData) return null;

    const { bidAmount, composition, approvedprojects, singleCard, pmr, sectors, additionalData } = sectionData;

    const dataSize = sectors.length;

    return (
      <div className="section section-tcp content-background">
        <header className="header-content">
          <p>{t('one_pager_report.tcp_title')}</p>
          <ReusableIcon
            title={title}
            color="#66B6B6"
          />
        </header>

        <div className={`c-charts-tcp ${isMinibizableGrid(sectors) ? "reduce-grid" : generalData.myPortfolio === "Global" ? "global-grid" : ""}`}>
          {/* Composition */}
          <div className="composition content-composition">
            <div className="chart-row">
              <h3 className="chart-title">{t('one_pager_report.composition')}</h3>
              <div className="chart-content">
                <CardInfo title={t('one_pager_report.total_projects')} data={composition?.projects ?? 0} />
                <CardInfo title={t('one_pager_report.national_projects')} data={composition?.projectTotalCountNational ?? 0} />
                <CardInfo title={t('one_pager_report.regional_projects')} data={composition?.projectTotalCountRegional ?? 0} />
              </div>
            </div>
            <div className="chart-row mt-4">
              <div className="grid-chart-content">
                <CardInfo title={t('one_pager_report.operations')} data={composition?.operations ?? 0} />
                <CardInfo
                  title={t('one_pager_report.years')}
                  data={composition?.years ?? 0}
                  units={
                    (composition?.years ?? 0) > 1
                      ? t('one_pager_report.antiquity_years')
                      : t('one_pager_report.antiquity_year')
                  }
                />
              </div>
            </div>
          </div>

          {/* BID Amount */}
          <div className="amounts">
            <div>
              <h3 className="chart-title">{t('one_pager_report.bid_amount')}</h3>
              <div className="chart-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                <CardInfo
                  title={t('one_pager_report.original')}
                  data={`${formatMillions(bidAmount?.originalAmount)} MM`}
                />
                <CardInfo
                  title={t('one_pager_report.current_title')}
                  data={`${formatMillions(bidAmount?.currentAmount)} MM`}
                />
              </div>
            </div>
            <div>
              <h3 className="chart-title">{t('one_pager_report.accumulate_disbursements')}</h3>
              <div className="chart-content">
                <ChartProgressBar
                  completed={bidAmount?.disbursementPercent}
                  totalAmount={bidAmount?.disbursementAmount + bidAmount?.undisburstmentAmount}
                  disbursmentAmount={bidAmount?.disbursementAmount}
                />
              </div>
            </div>
          </div>

          <CompositionWalletCard
            sectors={sectors}
            colors={gradientColors.slice(0, dataSize)}
            dataSize={dataSize}
            isPdfExport={isPdfExport}
          />

          <div className="taxonomy">
            <h3 className="chart-title">{t('one_pager_report.taxonomy')}</h3>
            <div className="chart-content">
              <CardInfo
                title={t('one_pager_report.customer_support')}
                data={additionalData?.taxonomy?.customerSupportPercent ?? 0}
                units={"%"}
              />
              <CardInfo
                title={t('one_pager_report.support_loan')}
                data={additionalData?.taxonomy?.loanPortfolioSupportPercent ?? 0}
                units={"%"}
              />
              <CardInfo
                title={t('one_pager_report.research_and_dissemination')}
                data={additionalData?.taxonomy?.researchDisseminationPercent ?? 0}
                units={"%"}
              />
            </div>
          </div>

          <div className="participation">
            <h3 className="chart-title">{t('one_pager_report.participation')}</h3>
            <div className="chart-content">
              <CardInfo title={t('one_pager_report.urd')} data={additionalData?.participation?.urdOffice ?? 0} />
              <CardInfo
                title={t('one_pager_report.8_contriues')}
                data={additionalData?.participation?.participant8Countries ?? 0}
              />
              <CardInfo
                title={t('one_pager_report.more_8_contriues')}
                data={additionalData?.participation?.participantPlus8Countries ?? 0}
              />
            </div>
          </div>

          <div className="execution">
            <h3 className="chart-title">{t('one_pager_report.execution')}</h3>
            <div className="chart-content">
              <CardInfo
                title={t('one_pager_report.exceute_by_bank')}
                data={additionalData?.execution?.executedByBankPercent ?? 0}
                units={"%"}
              />
              <CardInfo
                title={t('one_pager_report.exceute_by_agency')}
                data={additionalData?.execution?.executedByExecutingAgencyPercent ?? 0}
                units={"%"}
              />
            </div>
          </div>

          <PendingCard
            firstData={singleCard.pendingLegalValidity}
            firstTitle={t('one_pager_report.pending_legal_validity')}
          />

          <PendingCard
            className="pending-legal"
            firstData={singleCard.projectsOverdueDisbursement}
            firstTitle={t('one_pager_report.projects_with_deadlines')}
          />
        </div>
      </div>
    );
  };


  return (
    <section className="c-main">
      <HeaderReport
        portfolioName={portfolioName}
        filtersApplied={filtersApplied}
        generalData={generalData}
      />

      <article className="content">{data.tcp && renderSection(data.tcp, title)}</article>
    </section>
  );
};

export default OnePagerReportTcp;
